import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '@/shared/billing/currency';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { IPanelPagoRepository } from '@/domain/repositories/IPanelPagoRepository';
import { useToast } from '@/presentation/contexts/ToastContext';
import { Button } from '@/presentation/components/common';
import { ModalShell } from '@/presentation/components/common/ModalShell';
import type { TenantGridDTO } from '@/domain/repositories/ITenantRepository';
import type { PanelPagoFormData } from '@/domain/repositories/IPanelPagoRepository';
import type { PanelPago } from '@/domain/entities/PanelPago';

const isTenantGuid = (id: string | number): id is string =>
  typeof id === 'string' && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(id);

interface RegisterPaymentModalProps {
  isOpen: boolean;
  tenant: TenantGridDTO;
  onClose: () => void;
  onSuccess: () => void;
}

// Mock de tipos de pago - en producción vendría de API
const mockPaymentTypes = [
  { id: 1, descripcion: 'Transferencia Bancaria' },
  { id: 2, descripcion: 'Efectivo' },
  { id: 3, descripcion: 'Tarjeta de Crédito' },
  { id: 4, descripcion: 'Cheque' },
  { id: 5, descripcion: 'Mercado Pago' },
];


const periodLabel = (periodo: string) => {
  const [y, m] = periodo.split('-').map(Number);
  if (!y || !m) return periodo;
  return new Date(y, m - 1, 1).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
};

/**
 * Registra el cobro real de una factura confirmada impaga.
 * El monto es el saldo de la factura (mes + arrastre de impagas anteriores) y no se edita acá.
 */
export const RegisterPaymentModal: React.FC<RegisterPaymentModalProps> = ({
  isOpen,
  tenant,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
  const [unpaid, setUnpaid] = useState<PanelPago[]>([]);
  const [periodo, setPeriodo] = useState('');
  const [fechaPago, setFechaPago] = useState<Date | null>(new Date());
  const [medioPago, setMedioPago] = useState<number | null>(null);
  const [notas, setNotas] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setFechaPago(new Date());
    setMedioPago(null);
    setNotas('');
    setSelectedFile(null);

    const load = async () => {
      setIsLoadingInvoices(true);
      try {
        const repository = container.get<IPanelPagoRepository>(TYPES.IPanelPagoRepository);
        const cuotas = await repository.getByTenantId(tenant.id);
        const impagas = cuotas
          .filter((c) => (c.deudaAcumulada || 0) > 0)
          // Facturas individuales: se paga siempre primero la más antigua.
          .sort((a, b) => a.fechaPeriodoPago.localeCompare(b.fechaPeriodoPago));
        if (cancelled) return;
        setUnpaid(impagas);
        setPeriodo(impagas[0]?.fechaPeriodoPago ?? '');
      } catch (err) {
        console.error('Error loading facturas impagas:', err);
        if (!cancelled) {
          setUnpaid([]);
          setPeriodo('');
          showError(t('pages.tenants.errorLoadingPayment'));
        }
      } finally {
        if (!cancelled) setIsLoadingInvoices(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tenant.id]);

  const selected = useMemo(
    () => unpaid.find((c) => c.fechaPeriodoPago === periodo) ?? null,
    [unpaid, periodo]
  );
  const amountDue = selected?.deudaAcumulada ?? 0;
  const moneda = selected?.moneda ?? 'ARS';
  const money = (n: number) => formatMoney(n, moneda);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selected) {
      showError(t('pages.tenants.noConfirmedInvoice'));
      return;
    }
    if (!fechaPago) {
      showError(t('pages.tenants.validation.paymentDateRequired'));
      return;
    }
    if (fechaPago > new Date()) {
      showError(t('pages.tenants.validation.paymentDateNotFuture'));
      return;
    }
    if (!medioPago) {
      showError(t('pages.tenants.validation.paymentMethodRequired'));
      return;
    }

    setIsLoading(true);
    try {
      const repository = container.get<IPanelPagoRepository>(TYPES.IPanelPagoRepository);
      const paymentData: PanelPagoFormData = {
        ...(isTenantGuid(tenant.id) ? { tenantId: tenant.id } : { clinicaId: Number(tenant.id) }),
        fechaPago,
        fechaPeriodoPago: selected.fechaPeriodoPago,
        monto: amountDue,
        planId: selected.planId ?? null,
        medioPago,
        notas,
        nombreArchivoComprobante: selectedFile?.name,
      };

      await repository.create(paymentData);
      success(t('pages.tenants.paymentRegisteredSuccess'));
      onSuccess();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Error al registrar pago');
      showError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const fieldClass =
    'block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 disabled:cursor-not-allowed disabled:bg-slate-100 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:disabled:bg-gray-600';

  const labelClass = 'mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300';

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={t('pages.tenants.registerPayment')}
      subtitle={tenant.nombre}
      closeOnOverlay={!isLoading}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form="register-payment-form"
            variant="primary"
            isLoading={isLoading}
            disabled={isLoadingInvoices || isLoading || !selected}
            className="w-full sm:w-auto"
          >
            {t('pages.tenants.registerPayment')}
          </Button>
        </>
      }
    >
      <form id="register-payment-form" onSubmit={handleSubmit} className="space-y-3">
        {isLoadingInvoices ? (
          <div className="py-10 text-center text-sm text-slate-500 dark:text-slate-400">
            {t('common.loading')}
          </div>
        ) : unpaid.length === 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-100">
            {t('pages.tenants.noConfirmedInvoice')}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor="reg-pay-period">
                  {t('pages.tenants.invoiceToPay')}
                </label>
                <select
                  id="reg-pay-period"
                  value={periodo}
                  onChange={(e) => setPeriodo(e.target.value)}
                  disabled={unpaid.length === 1}
                  className={fieldClass}
                  required
                >
                  {unpaid.map((c, index) => (
                    <option key={c.id} value={c.fechaPeriodoPago} disabled={index > 0}>
                      {periodLabel(c.fechaPeriodoPago)} · {c.estadoPagoDescripcion}
                    </option>
                  ))}
                </select>
                {unpaid.length > 1 && (
                  <p className="mt-1 text-[11px] leading-snug text-slate-500 dark:text-slate-400">
                    {t('pages.tenants.payOldestFirst', { count: unpaid.length })}
                  </p>
                )}
              </div>

              <div>
                <label className={labelClass} htmlFor="reg-pay-date">
                  {t('pages.tenants.paymentDate')}
                </label>
                <input
                  id="reg-pay-date"
                  type="date"
                  value={fechaPago ? new Date(fechaPago).toISOString().split('T')[0] : ''}
                  onChange={(e) => setFechaPago(e.target.value ? new Date(e.target.value) : null)}
                  max={new Date().toISOString().split('T')[0]}
                  required
                  className={fieldClass}
                />
              </div>

              <div className="sm:col-span-2">
                <label className={labelClass} htmlFor="reg-pay-method">
                  {t('pages.tenants.paymentMethod')}
                </label>
                <select
                  id="reg-pay-method"
                  value={medioPago || ''}
                  onChange={(e) => setMedioPago(e.target.value ? parseInt(e.target.value, 10) : null)}
                  className={fieldClass}
                  required
                >
                  <option value="">{t('pages.tenants.selectPaymentMethod')}</option>
                  {/* Mercado Pago cobra solo en pesos: no aplica a facturas en dólares. */}
                  {mockPaymentTypes.filter((type) => moneda === 'ARS' || type.id !== 5).map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.descripcion}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 dark:border-gray-600 dark:bg-gray-900/40">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t('pages.tenants.amount')}
                </span>
                <span className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">
                  {money(amountDue)}
                </span>
              </div>
              {moneda === 'USD' && (
                <p className="mt-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                  {t('pages.tenants.invoiceInUsd')}
                </p>
              )}
              <p className="mt-1 text-[11px] leading-snug text-slate-500 dark:text-slate-400">
                {t('pages.tenants.amountFromInvoice')}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelClass} htmlFor="reg-pay-notes">
                  {t('pages.tenants.notes')} ({t('common.optional')})
                </label>
                <textarea
                  id="reg-pay-notes"
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  className={`${fieldClass} min-h-[4.5rem] resize-y`}
                  rows={2}
                />
              </div>

              <div className="sm:col-span-2">
                <label className={labelClass} htmlFor="reg-pay-file">
                  {t('pages.tenants.receipt')} ({t('common.optional')})
                </label>
                <input
                  id="reg-pay-file"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-slate-500 file:mr-3 file:rounded-lg file:border-0 file:bg-teal-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-teal-700 hover:file:bg-teal-100 dark:text-slate-400 dark:file:bg-teal-900/40 dark:file:text-teal-200"
                />
                {selectedFile && (
                  <p className="mt-1 truncate text-[11px] text-slate-500 dark:text-slate-400">
                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </form>
    </ModalShell>
  );
};
